import { logger } from '@adornis/base/logging.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { getGlobalConfig } from '@adornis/bh-matching-config/api/global-config.js';
import { GlobalConfig } from '@adornis/bh-matching-config/entities/global-config.js';
import { TimeframeAPI } from '@adornis/bh-timeframes/api/TimeframeAPI.js';
import { BHPageAdminEinrichtungTimeframes } from '@adornis/bh-timeframes/components/bh-page-admin-einrichtung-timeframes.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/tiptap/client/x-tiptap.js';
import { addYears, differenceInDays } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { from } from 'rxjs';
import { MatchingTimeframeAPI } from '../api/MatchingTimeframeAPI.js';
import { MatchingPromptConfirmCleanup } from '../client/prompt/matching-prompt-confirm-cleanup.js';

/*
 * @element matching-page-timeframes
 */
@customElement('matching-page-timeframes')
export class MatchingPageTimeframes extends BHPageAdminEinrichtungTimeframes {
  @state() protected readonly _size = new WindowSizeController(this);

  @state() protected readonly _form = new FormController(
    this,
    from(getGlobalConfig()(selectionSet(() => GlobalConfig, 2))),
    {
      success: async config => {
        try {
          await TrDialog.waitFor(config.save());
          void XSnackbar.show('Texte wurden erfolgreich angepasst.');
        } catch (err) {
          logger.error({ err });
          void TrDialog.alert('Es gab ein Problem beim Speichern der Texte. Bitte versuchen Sie es später erneut.');
        }
      },
    },
  );

  @state() protected readonly _isExpiredForCleanup = new RXController(this, MatchingTimeframeAPI.findForCleanup()());

  protected isFrameExpired(timeframe: Timeframe) {
    return differenceInDays(addYears(timeframe.from, 1), new Date()) < 0;
  }

  protected isExpiredAndNotEmpty(timeframe: Timeframe) {
    return (this._isExpiredForCleanup.value ?? []).includes(timeframe._id);
  }

  protected _isCleanupBtnVisible(timeframe: Timeframe) {
    return this.isExpiredAndNotEmpty(timeframe) && this.isFrameExpired(timeframe);
  }

  override async activateTimeframe(timeframe: NarrowedEntity<Timeframe, { _id: 1; isActive: 1 }>, silent?: boolean) {
    if (!this.institutionID.value) return;

    if (!silent) {
      const isConfirmed = await TrDialog.confirm(html`
        <tr-flex space="sm" ${css({ paddingBottom: this.spacing.md })}>
          <tr-h3 bold>${this._translation.translate('timeframe')} ${this._translation.translate('activate')}?</tr-h3>
          <tr-text>${this._translation.translate('timeframe', 'activate_registration_wrn')}</tr-text>
        </tr-flex>
      `);
      if (!isConfirmed) return;
    }

    if (timeframe.isActive) return;
    try {
      await TrDialog.waitFor(TimeframeAPI.activate(timeframe._id)());
      timeframe.isActive = true;
      this.requestUpdate();
    } catch (err) {
      logger.error({ err }, 'Error activating timeframe');
      void TrDialog.alert('Es gab ein Problem beim Aktivieren des Zeitraums. Bitte versuchen Sie es später erneut.');
    }
  }

  protected override _content() {
    return html`
      <tr-flex space="md">
        <x-h1 page-title> ${this._translation.translate('timeframe', 'noticeForClosedRegistration')} </x-h1>
        <tr-grid columns="1fr 1fr" space="sm">
          <tr-text bold> ${this._translation.translate('enums', 'GERMAN')} </tr-text>
          <tr-text bold> ${this._translation.translate('enums', 'ENGLISH')} </tr-text>
          <tr-tiptap ${css({ height: '100%' })} ${this._form.field('signupClosedNoteGerman')}></tr-tiptap>
          <tr-tiptap ${css({ height: '100%' })} ${this._form.field('signupClosedNoteEnglish')}></tr-tiptap>
        </tr-grid>
        <tr-flex horizontal end>
          <tr-button icon="save" @click=${(e: MouseEvent) => this._form.validate({ mouseEvent: e })}>
            Speichern
          </tr-button>
        </tr-flex>
      </tr-flex>
      <tr-flex horizontal crossaxis-center space="md">
        <tr-icon ${css({ color: this.colors.tone.warning })}> warning </tr-icon>
        <tr-text tone="warning"> Es darf nur ein aktives Semester ausgewählt sein. </tr-text>
      </tr-flex>
      ${super._content()}
    `;
  }

  protected _renderTimeframeCleanupBtn(timeframe: Timeframe) {
    const isTimeframeExpired = this.isExpiredAndNotEmpty(timeframe);

    return html`
      <tr-flex>
        <tr-button-small
          .color=${isTimeframeExpired ? this.colors.tone.error : this.colors.accent}
          icon="cleaning_services"
          .mode=${isTimeframeExpired ? 'fill' : 'outline'}
          @click=${() =>
            this._timeframeUserCleanup(timeframe).then(() =>
              // this is a hack, but it works. Watching is no option here
              window.location.reload(),
            )}
        >
          ${this._size.tablet ? nothing : this._translation.translate('timeframe', 'cleanupUserData')}
        </tr-button-small>
        <tr-tooltip .text=${'Alle Mentor/Mentee bezogene Daten löschen - DSGVO konform'} placement="bottom">
        </tr-tooltip>
      </tr-flex>
    `;
  }

  protected _timeframeUserCleanup(timeframe: Timeframe) {
    return MatchingPromptConfirmCleanup.showPopup({ props: { timeframe } });
  }

  override _renderTimeframeActionsAccent(timeframe: Timeframe) {
    const displayCleanBtn = !this.isExpiredAndNotEmpty(timeframe) && !this.isFrameExpired(timeframe);
    let newActionsTemplList = [super._renderTimeframeActionsAccent(timeframe)];

    if (displayCleanBtn) newActionsTemplList = [this._renderTimeframeCleanupBtn(timeframe), ...newActionsTemplList];

    return html`${newActionsTemplList}`;
  }

  override _renderTimeframeActionsRedColor(timeframe: Timeframe) {
    const displayCleanBtn = this.isExpiredAndNotEmpty(timeframe) && this.isFrameExpired(timeframe);
    let newActionsTemplList = [super._renderTimeframeActionsRedColor(timeframe)];

    if (displayCleanBtn) newActionsTemplList = [...newActionsTemplList, this._renderTimeframeCleanupBtn(timeframe)];

    return html`${newActionsTemplList}`;
  }
}
